import { useState } from 'react'
import Heading from '@/common/shared/heading'
import { Separator } from '@/common/ui/separator'
import { Button } from '@/common/ui/button'
import { Link } from 'react-router-dom'
import { GetPaymentGateWayResponseData } from '@/modules/bank/type'
import PaymentGatewayTable from '../PaymentGatewayTable'
import MaintenanceTable from '../MaintenanceTable'
import CreateMaintenanceModal from '../CreateMaintenanceModal'
import useQueryProfile from '@/modules/auth/api/hooks/useQueryProfile'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import useMutationSyncPaymentGateway from '@/modules/bank/api/hooks/useMutationSyncPaymentGateway'
import useQueryGetPaymentGatewayConstraints from '@/modules/bank/api/hooks/useQueryGetPaymentGatewayConstraints'
import useQueryGetMaintenancesAll from '@/modules/bank/api/hooks/useQueryGetMaintenancesAll'
import { UseFallbackBankToggle } from '../UseFallbackBankToggle'
import { EnableDepositChannelChoiceToggle } from '../EnableDepositChannelChoiceToggle'
import { Spinner } from '@/common/ui/spinner'
import useQueryGetGeneralSetting from '@/modules/client-setting/api/hooks/useQueryGetGeneralSetting'
import useMutationUpdateGeneralSetting from '@/modules/client-setting/api/hooks/useMutationUpdateGeneralSetting'
import { GeneralSettingQueryKeys } from '@/modules/client-setting/keys'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { cn } from '@/common/lib/utils'
import LoadingOverlay from '@/common/components/LoadingOverlay'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/common/ui/tabs'
import { AlertTriangle, Wallet, Wrench } from 'lucide-react'
import type { TabValue } from '@/modules/bank/pages/PaymentGatewayPage'
import type { ChannelCountsResponse } from '@/modules/bank/type'

interface PaymentGatewayListingProps {
	data: GetPaymentGateWayResponseData[]
	total?: number
	isLoading?: boolean
	activeTab: TabValue
	onTabChange: (tab: TabValue) => void
	channelCounts?: ChannelCountsResponse | null
}

export default function PaymentGatewayListing({
	data,
	total = 0,
	isLoading = false,
	activeTab,
	onTabChange,
	channelCounts,
}: PaymentGatewayListingProps) {
	const { data: user } = useQueryProfile()
	const hasWritePermission = user?.userGroup.writePermission.includes(
		PermissionKeys.MANAGE_BANK_ACCOUNT,
	)

	const queryClient = useQueryClient()
	const { mutate: syncPaymentGateway, isPending: isSyncing } =
		useMutationSyncPaymentGateway()
	const [maintenanceModalOpen, setMaintenanceModalOpen] = useState(false)
	const { data: maintenancesData, isLoading: isLoadingMaintenances } =
		useQueryGetMaintenancesAll()
	const { data: generalSetting } = useQueryGetGeneralSetting()
	const {
		mutateAsync: updateGeneralSetting,
		isPending: isUpdatingWithdrawMode,
	} = useMutationUpdateGeneralSetting()

	// Fetch constraints for fallback min/max values in table
	const { data: constraints } = useQueryGetPaymentGatewayConstraints()

	const useAutoWithdrawBank = generalSetting?.useAutoWithdrawBank ?? true

	const showWithdrawBankMissingWarning =
		useAutoWithdrawBank &&
		channelCounts != null &&
		channelCounts.activeWithdrawBank === null

	const handleWithdrawModeChange = async (value: boolean) => {
		if (!hasWritePermission) return
		try {
			await updateGeneralSetting({ payload: { useAutoWithdrawBank: value } })
			queryClient.invalidateQueries({
				queryKey: [GeneralSettingQueryKeys.GetGeneralSetting],
			})
			toast.success(
				value ? 'เปลี่ยนเป็นถอนออโต้แล้ว' : 'เปลี่ยนเป็นถอนแมนวลแล้ว',
			)
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	return (
		<div className="space-y-6">
			<LoadingOverlay
				open={isSyncing}
				message="กำลังซิงค์ข้อมูลธนาคารออโต้..."
			/>
			<div className="flex items-start justify-between">
				<Heading title={`ธนาคารออโต้`} description="รายการธนาคารออโต้" />
				<div className="flex flex-col items-center gap-2 sm:flex-row">
					{hasWritePermission && (
						<Button
							variant="outline"
							onClick={() => {
								syncPaymentGateway()
							}}
							disabled={isSyncing}
							className="min-w-[140px]"
						>
							{isSyncing ? (
								<>
									<Spinner className="mr-2 h-4 w-4" />
									กำลังซิงค์...
								</>
							) : (
								'Sync อัพเดตเงิน'
							)}
						</Button>
					)}
					{hasWritePermission && (
						<Button asChild variant="default">
							<Link to="/operator/payment-gateway/create-payment-gateway">
								เพิ่มธนาคารออโต้
							</Link>
						</Button>
					)}
				</div>
			</div>
			<Separator />
			<UseFallbackBankToggle />
			<EnableDepositChannelChoiceToggle />

			{/* Tab UI: บัญชีถอน clearly grouped with QR Payment + โอนเงินผ่านธนาคาร; other channels separate */}
			<Tabs
				value={activeTab}
				onValueChange={(v) => onTabChange(v as TabValue)}
				className="w-full"
			>
				<div className="flex flex-wrap items-end gap-6">
					{/* Group 1: 3-col — บน: QR Payment | โอนเงินผ่านธนาคาร | P2P, ล่าง: บัญชีถอน */}
					<div className="flex flex-col gap-2">
						<p className="text-xs font-medium text-muted-foreground">
							บัญชีถอน ใช้ร่วมกัน 3 ช่องทางนี้
						</p>
						<TabsList className="grid h-auto max-w-lg grid-cols-3 gap-2 rounded-lg border border-muted-foreground/20 bg-muted/40 p-2">
							<TabsTrigger
								value="QR"
								className="data-[state=active]:bg-background data-[state=active]:shadow-sm"
							>
								QR Payment
								{channelCounts != null && (
									<span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs">
										{channelCounts.qr}
									</span>
								)}
							</TabsTrigger>
							<TabsTrigger
								value="TRANSFER"
								className="data-[state=active]:bg-background data-[state=active]:shadow-sm"
							>
								โอนเงินผ่านธนาคาร
								{channelCounts != null && (
									<span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs">
										{channelCounts.transfer}
									</span>
								)}
							</TabsTrigger>
							<TabsTrigger
								value="P2P"
								className="data-[state=active]:bg-background data-[state=active]:shadow-sm"
							>
								P2P
								{channelCounts != null && (
									<span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs">
										{channelCounts.p2p}
									</span>
								)}
							</TabsTrigger>
							<TabsTrigger
								value="WITHDRAW_BANK"
								className="col-span-3 flex flex-wrap items-center justify-center gap-1 data-[state=active]:bg-background data-[state=active]:shadow-sm"
							>
								<span className="inline-flex items-center gap-1.5">
									บัญชีถอนเงิน
									{showWithdrawBankMissingWarning && (
										<AlertTriangle
											className="h-4 w-4 shrink-0 text-destructive"
											aria-hidden
										/>
									)}
								</span>
								{channelCounts != null && (
									<span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs">
										{channelCounts.withdrawBank}
									</span>
								)}
							</TabsTrigger>
						</TabsList>
					</div>

					{/* Group 2: ทรู มันนี่ วอลเล็ท */}
					<div className="flex flex-col gap-2">
						<TabsList className="inline-flex h-auto rounded-lg border border-muted-foreground/20 bg-muted/40 p-1.5">
							<TabsTrigger
								value="TRUE_MONEY"
								className="min-w-[140px] data-[state=active]:bg-background data-[state=active]:shadow-sm"
							>
								<span className="font-medium">ทรู มันนี่ วอลเล็ท</span>
								{channelCounts != null && (
									<span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs">
										{channelCounts.trueMoney}
									</span>
								)}
							</TabsTrigger>
						</TabsList>
					</div>

					{/* การซ่อมบำรุง — ขวาสุดโดดๆ */}
					<div className="ml-auto flex flex-col gap-2">
						<p className="text-xs font-medium text-muted-foreground">&nbsp;</p>
						<TabsList className="inline-flex h-auto rounded-lg border border-muted-foreground/20 bg-muted/40 p-1.5">
							<TabsTrigger
								value="maintenance"
								className="data-[state=active]:bg-background data-[state=active]:shadow-sm"
							>
								การซ่อมบำรุง
								{maintenancesData?.data && maintenancesData.data.length > 0 && (
									<span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs">
										{maintenancesData.data.length}
									</span>
								)}
							</TabsTrigger>
						</TabsList>
					</div>
				</div>

				<TabsContent value="QR" className="mt-4">
					<PaymentGatewayTable
						data={data}
						total={total}
						constraints={constraints}
						channel="QR"
						isLoading={isLoading}
					/>
				</TabsContent>

				<TabsContent value="WITHDRAW_BANK" className="mt-4">
					<div className="mb-4 flex flex-wrap items-center gap-4">
						<span className="text-sm font-medium text-muted-foreground">
							โหมดถอน:
						</span>
						<div className="flex rounded-lg border border-muted-foreground/20 bg-muted/40 p-1">
							<Button
								type="button"
								variant={useAutoWithdrawBank ? 'secondary' : 'ghost'}
								size="sm"
								className={cn(
									useAutoWithdrawBank && 'bg-background font-medium shadow-sm',
								)}
								onClick={() => handleWithdrawModeChange(true)}
								disabled={isUpdatingWithdrawMode || !hasWritePermission}
							>
								ถอนออโต้
							</Button>
							<Button
								type="button"
								variant={!useAutoWithdrawBank ? 'secondary' : 'ghost'}
								size="sm"
								className={cn(
									!useAutoWithdrawBank && 'bg-background font-medium shadow-sm',
								)}
								onClick={() => handleWithdrawModeChange(false)}
								disabled={isUpdatingWithdrawMode || !hasWritePermission}
							>
								ถอนแมนวล
							</Button>
						</div>
						{!useAutoWithdrawBank && (
							<span className="text-xs text-muted-foreground">
								กำลังใช้ถอนแมนวล — ตารางบัญชีถอนจะไม่ถูกใช้งาน
							</span>
						)}
					</div>
					{useAutoWithdrawBank &&
						channelCounts != null &&
						channelCounts.activeWithdrawBank && (
							<div
								className="mb-4 flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 dark:border-blue-800 dark:bg-blue-950/50"
								role="status"
							>
								<Wallet className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
								<span className="text-sm font-medium text-blue-800 dark:text-blue-200">
									บัญชีถอนปัจจุบัน:{' '}
									{channelCounts.activeWithdrawBank.displayName} (
									{channelCounts.activeWithdrawBank.bankProviderName})
								</span>
							</div>
						)}
					{showWithdrawBankMissingWarning && (
						<div
							className="mb-4 flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-2.5"
							role="alert"
						>
							<AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
							<span className="text-sm font-medium text-destructive">
								ยังไม่ได้เลือกบัญชีถอนเงิน กรุณาเลือกบัญชีถอนเงินในตาราง
							</span>
						</div>
					)}
					<p className="mb-4 text-sm text-muted-foreground">
						กำหนดบัญชีที่ใช้ถอนเงินสำหรับช่องทางธนาคาร
					</p>
					<div
						className={cn(
							'transition-all duration-200',
							!useAutoWithdrawBank && 'pointer-events-none select-none blur-sm',
						)}
					>
						<PaymentGatewayTable
							data={data}
							total={total}
							constraints={constraints}
							channel="WITHDRAW_BANK"
							isLoading={isLoading}
						/>
					</div>
				</TabsContent>

				<TabsContent value="TRANSFER" className="mt-4">
					<PaymentGatewayTable
						data={data}
						total={total}
						constraints={constraints}
						channel="TRANSFER"
						isLoading={isLoading}
					/>
				</TabsContent>

				<TabsContent value="TRUE_MONEY" className="mt-4">
					<PaymentGatewayTable
						data={data}
						total={total}
						hideLinkColumn={true}
						constraints={constraints}
						channel="TRUE_MONEY"
						isLoading={isLoading}
					/>
				</TabsContent>

				<TabsContent value="P2P" className="mt-4">
					<p className="mb-4 text-sm text-muted-foreground">
						ช่องทาง P2P จับคู่กับผู้ถอน
					</p>
					<PaymentGatewayTable
						data={data}
						total={total}
						constraints={constraints}
						channel="P2P"
						isLoading={isLoading}
					/>
				</TabsContent>

				<TabsContent value="maintenance" className="mt-4">
					<div className="flex items-center justify-between gap-4">
						<p className="text-sm text-muted-foreground">
							รายการซ่อมบำรุงชั่วคราว
							สามารถยกเลิกแต่ละรายการได้หากผู้ให้บริการกลับมาให้บริการก่อนเวลา
						</p>
						{hasWritePermission && (
							<Button
								variant="default"
								onClick={() => setMaintenanceModalOpen(true)}
								className="gap-2"
							>
								<Wrench className="h-4 w-4" />
								สร้างการซ่อมบำรุง
							</Button>
						)}
					</div>
					<div className="mt-4">
						<MaintenanceTable
							data={maintenancesData?.data ?? []}
							isLoading={isLoadingMaintenances}
						/>
					</div>
				</TabsContent>
			</Tabs>

			<CreateMaintenanceModal
				open={maintenanceModalOpen}
				onOpenChange={setMaintenanceModalOpen}
			/>
		</div>
	)
}
